import { useCallback, useRef, useSyncExternalStore } from 'react';
import { expandRect, rectContains, type Rect } from '../../doc/geometry';
import { useBoard, usePaneStore } from '../context';
import { visibleWorldRect } from './camera';

/**
 * Overscan as a fraction of the viewport on each side. The mounted set covers this larger
 * area and is only recomputed once the viewport leaves it (or the zoom changes a lot), so a
 * pan does not mount/unmount items every frame and nothing pops in at the edges.
 */
const OVERSCAN = 0.6;

interface Cover {
  rect: Rect | null;
  zoom: number;
  ids: string[];
}

/**
 * Ids of items to mount, in z order. The returned array is referentially stable while the
 * covered area is unchanged, so panning does not re-render the item list.
 */
export function useVisibleItemIds(): string[] {
  const board = useBoard();
  const { index } = board;
  const pane = usePaneStore();
  const cover = useRef<Cover>({ rect: null, zoom: 1, ids: [] });
  const dirty = useRef(true);

  const subscribe = useCallback(
    (cb: () => void) => {
      const a = index.subscribe(() => {
        dirty.current = true;
        cb();
      });
      const b = pane.subscribe((s, prev) => {
        if (s.camera !== prev.camera || s.size !== prev.size) cb();
      });
      return () => {
        a();
        b();
      };
    },
    [index, pane],
  );

  const getSnapshot = useCallback(() => {
    const { camera, size } = pane.getState();
    const c = cover.current;
    if (!size.w || !size.h) return c.ids;
    const view = visibleWorldRect(camera, size);
    const zoomRatio = camera.zoom / c.zoom;
    if (!dirty.current && c.rect && rectContains(c.rect, view) && zoomRatio > 0.5 && zoomRatio < 2) return c.ids;
    const rect = expandRect(view, OVERSCAN);
    const ids = index.query(rect).filter((i) => !board.isHidden(i)).map((i) => i.id);
    dirty.current = false;
    // keep the previous array when the set and order did not change
    const same = ids.length === c.ids.length && ids.every((id, i) => id === c.ids[i]);
    cover.current = { rect, zoom: camera.zoom, ids: same ? c.ids : ids };
    return cover.current.ids;
  }, [board, index, pane]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
